import type React from "react";
import { useTheme } from "@/lib/theme";

/**
 * Theme-aware values for recharts. recharts needs concrete colour strings
 * (it can't read CSS variables reliably inside SVG attributes), so we branch
 * on the active theme here rather than leaning on Tailwind classes.
 */
export interface ChartTheme {
  grid: string;
  axis: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
}

export function useChartTheme(): ChartTheme {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  return {
    grid: isDark ? "hsl(0 0% 26%)" : "#F0F0F0",
    axis: isDark ? "hsl(0 0% 58%)" : "hsl(0 0% 40%)",
    tooltipBg: isDark ? "hsl(0 0% 17%)" : "#ffffff",
    tooltipBorder: isDark ? "hsl(0 0% 26%)" : "hsl(0 0% 88%)",
    tooltipText: isDark ? "hsl(0 0% 92%)" : "hsl(0 0% 7.8%)",
  };
}

interface TooltipPayloadItem {
  name?: string;
  value?: number | string;
  color?: string;
  dataKey?: string | number;
}

/**
 * Branded tooltip card — replaces recharts' default light box so it reads
 * correctly in both themes. Shows the hovered label and each series value.
 */
export function ChartTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayloadItem[];
  label?: string | number;
}): React.JSX.Element | null {
  const t = useChartTheme();
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div
      className="rounded-lg px-3 py-2 shadow-md"
      style={{
        background: t.tooltipBg,
        border: `1px solid ${t.tooltipBorder}`,
        color: t.tooltipText,
      }}
    >
      {label != null && (
        <p className="text-[11px] font-body text-muted-foreground mb-1">{String(label)}</p>
      )}
      {payload.map((item, i) => (
        <p key={i} className="flex items-center gap-2 text-sm font-head font-medium">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: item.color }}
            aria-hidden="true"
          />
          {item.name ? <span className="text-muted-foreground">{item.name}</span> : null}
          <span className="num ml-auto">{String(item.value)}</span>
        </p>
      ))}
    </div>
  );
}
